import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";
import { api } from "../lib/api";
import type { BootstrapData, CreateKind } from "../types";

interface ToastState {
  message: string;
  tone: "success" | "error" | "info";
}

export interface ModalDefaults {
  dueDate?: string;
  pinned?: boolean;
}

interface AppContextValue {
  data: BootstrapData | null;
  dataRevision: number;
  loading: boolean;
  error: string | null;
  modal: CreateKind | "quick" | "search" | null;
  modalDefaults: ModalDefaults | null;
  toast: ToastState | null;
  openModal: (modal: CreateKind | "quick" | "search", defaults?: ModalDefaults) => void;
  closeModal: () => void;
  refresh: () => Promise<boolean>;
  mutate: <T>(action: () => Promise<T>, successMessage: string) => Promise<T | null>;
  notify: (message: string, tone?: ToastState["tone"]) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [dataRevision, setDataRevision] = useState(0);
  const refreshSequence = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<AppContextValue["modal"]>(null);
  const [modalDefaults, setModalDefaults] = useState<ModalDefaults | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const refresh = useCallback(async () => {
    const sequence = ++refreshSequence.current;
    try {
      const next = await api.bootstrap();
      if (sequence !== refreshSequence.current) return false;
      setData(next);
      setDataRevision((value) => value + 1);
      setError(null);
      return true;
    } catch (caught) {
      if (sequence === refreshSequence.current) {
        setError(caught instanceof Error ? caught.message : "无法读取数据");
      }
      return false;
    } finally {
      if (sequence === refreshSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const reload = () => { if (document.visibilityState === "visible") void refresh(); };
    const changed = (event: StorageEvent) => { if (event.key === "eo_data_changed_at") reload(); };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", reload);
    window.addEventListener("storage", changed);
    return () => {
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", reload);
      window.removeEventListener("storage", changed);
      ++refreshSequence.current;
    };
  }, [refresh]);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const notify = useCallback(
    (message: string, tone: ToastState["tone"] = "success") =>
      setToast({ message, tone }),
    []
  );

  const mutate = useCallback(
    async <T,>(action: () => Promise<T>, successMessage: string) => {
      try {
        const result = await action();
        const refreshed = await refresh();
        if (!refreshed) notify("操作已保存，但最新数据暂未刷新；请重试读取，不要重复提交。", "info");
        else if (successMessage) notify(successMessage, "success");
        return result;
      } catch (caught) {
        notify(caught instanceof Error ? caught.message : "操作未完成", "error");
        return null;
      }
    },
    [notify, refresh]
  );

  const openModal = useCallback(
    (nextModal: CreateKind | "quick" | "search", defaults?: ModalDefaults) => {
      setModalDefaults(defaults ?? null);
      setModal(nextModal);
    },
    []
  );

  const closeModal = useCallback(() => {
    setModal(null);
    setModalDefaults(null);
  }, []);
  const value = useMemo<AppContextValue>(
    () => ({
      data,
      dataRevision,
      loading,
      error,
      modal,
      modalDefaults,
      toast,
      openModal,
      closeModal,
      refresh,
      mutate,
      notify
    }),
    [closeModal, data, dataRevision, error, loading, modal, modalDefaults, mutate, notify, openModal, refresh, toast]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp must be used inside AppProvider");
  return value;
}
